import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { isMobile } from 'react-device-detect';
import { useSelector, useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { get } from 'lodash-es';
import moment from 'moment';
import {
  Body1,
  FlexBox,
  BaseBox,
  Body2,
  Heading4,
  Icons,
  DoconToggle,
  DoconInput,
  PrimaryButton,
  DoconDatePicker,
  DoconLabel,
  DoconRadioButton,
  DoconInputWrapper,
  message,
  ellipsis,
} from '@docon/docon-ui-library';
import RequestSuccessfulModal from './Modal/RequestSuccessful';
import RequestOptOutModal from './Modal/RequestOptOut';
import {
  whatsAppOptIn,
  checkWhatsAppConsent,
  updateAuthState,
  whatsAppOptOut,
  logout,
  updateUser,
  Status,
} from 'redux/reducer/auth';
import { storageService } from 'services';
import { cleverTapAnl } from 'utilities';
import Image from 'shared/Image';
import firebaseService from 'app/firebaseSetup';
import { downloadAppPromotion } from 'shared/DownloadApp';
import { WhatsAppIcon, LogoutIcon } from 'shared/icons';
import { borderColor, colors } from 'utilities/colorConfig';
import { EventWhatsappCommsToggleSelect } from 'utilities/clevertapEvent';

const avatar = '/images/user.png';

const Header = styled(FlexBox)`
  position: sticky;
  top: 0;
  height: 56px;
  align-items: center;
  padding: 0 5%;
  gap: 12px;
  z-index: 1;
  background-color: ${colors.white};
  border-bottom: 1px solid ${borderColor};

  ${PrimaryButton} {
    margin: 0;
    margin-left: auto;
    align-self: inherit;
  }
`;

const Container = styled(FlexBox)`
  margin: auto;
  max-width: 650px;
  padding: 16px;
  flex: 1;
  flex-direction: column;

  ${Image} { float: right; margin-bottom: 0 }
`;

const Footer = styled(BaseBox)`
  position: sticky;
  margin-top: 20px;
  bottom: 0;
  z-index: 1;
  background-color: ${colors.white};

  ${Body2} {
    margin-top: 12px;
    text-align: center;
  }
`;

const Banner = styled(FlexBox)`
  padding: 9px 14px;
  border-radius: 8px;
  border: 1px solid #eaf5ff;
  background-color: #eaf5ff;
  background-image: linear-gradient(#eaf5ff, #ffffff);
`;

const SubText = styled(Body1)`
  flex: 1;
  color: ${colors.primaryColor};
`;

const Form = styled(FlexBox)`
  gap: var(--inputGap);
  padding-top: 16px;
  width: 100%;

  ${DoconInputWrapper} {
    margin: 0;
  }

  ${DoconDatePicker} {
    width: 100%;
  }

  .radioButton {
    margin-top: auto;
    margin-bottom: auto;
    gap: 12px;
    --space: 0;
  }

  .inputRootClassName {
    width: calc(50% - var(--inputGap)/2);
  }

  label {
    ${ellipsis}
  }
`;

const Row = styled(FlexBox)`
  padding-top: 12px;
  font-weight: 600;
  align-items: center;
  height: 44px;
  gap: 12px;

  ${Image} {
    width: 24px;
    height: 24px;
  }
`;

const Root = styled(FlexBox)`
  position: relative;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  flex-direction: column;
  --inputGap: 14px;
  
  ${() => isMobile && css`
    --inputGap: 12px;
  `}
`;

const Avatar = ({ size, ...props }: any) => <Image width={size} height={size} {...props} />;

const whatsAppBody = {
  'patientID': null,
  'doctorID': null,
  'countryCode': '91',
  'phoneNumber': '',
  'source': 'MWEB',
  'platform': 'MOBILE',
  'createdBy': 'user',
};

type IProps = RouteComponentProps;
interface IState {
  toggleState: boolean;
  requestType: string;
  showConsentModal: boolean;
  showRequestModal: boolean;
}

enum Labels {
  firstName = 'First Name *',
  lastName = 'Last Name',
  dateOfBirth = 'Date of Birth *',
  gender = 'Gender *',
  officeID = 'Office Id *',
  email = 'Email Id',
  houseNo = 'Address',
  pinCode = 'Pincode',
  landmark = 'Street Name',
}

interface IForm {
  firstName: string;
  lastName: string;
  mobileNumber: any;
  gender: string;
  dateOfBirth: any;
  email: string;
  houseNo?: string;
  landmark?: string;
  pinCode?: number;
}

function Logout({
  history,
}: IProps) {
  const [state, setState] = useState<IState>({
    requestType: '',
    toggleState: false,
    showConsentModal: false,
    showRequestModal: false,
  });

  const [form, setForm] = useState<IForm>({
    firstName: '',
    lastName: '',
    mobileNumber: null,
    gender: '',
    dateOfBirth: '',
    email: '',
    houseNo: '',
    landmark: '',
    pinCode: undefined,
  });

  const {
    user,
    accessToken,
    logoutStatus,
    userUpdateError,
    userUpdateStatus,
    whatsAppConsentStatus,
    whatsAppOptType,
  } = useSelector(state => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!accessToken) history.replace('/');

    if (accessToken && user.userID) {
      setForm({
        firstName: user.firstName,
        lastName: user.lastName,
        mobileNumber: user.mobileNumber,
        gender: user.gender,
        dateOfBirth: user.dateOfBirth ? moment(user.dateOfBirth, 'YYYY-MM-DD hh:mm:ss ZZ') : undefined,
        email: user.email,
        houseNo: user.address?.houseNo,
        landmark: user.address?.landmark,
        pinCode: user.address?.pinCode,
      });

      dispatch(checkWhatsAppConsent(user.mobileNumber));
    }
  }, [accessToken]);

  useEffect(() => {
    return () => {
      dispatch(updateAuthState({ whatsAppOptType: '', userUpdateStatus: '', userUpdateError: '' }));
    };
  }, []);

  useEffect(() => {
    if (userUpdateStatus === Status.fulfilled)
      message.success('Profile update successfully');
  }, [userUpdateStatus]);

  useEffect(() => {
    const error = Object.keys(userUpdateError).map((key: string) => `${Labels[key].replace('*', '')}: ${get(userUpdateError, `[${key}][0]`, '')}`).join('\n');
    if (error)
      message.error(error);

  }, [userUpdateError]);

  useEffect(() => {
    setState(state => ({ ...state, toggleState: whatsAppConsentStatus === 'OPTED_IN' ? true : false }));
  }, [whatsAppConsentStatus]);

  useEffect(() => {
    if (accessToken && logoutStatus === Status.fulfilled) {
      storageService.clear();
      firebaseService.deRegisterToken();
      window.location.href = '/';
    }
  }, [logoutStatus]);

  useEffect(() => {
    if (whatsAppOptType) {
      setState({ ...state, showRequestModal: false });
      cleverTapAnl.event({
        event_name: 'whatsapp_comms_toggle_select',
        event: {
          page_name: 'user_profile',
          whatsapp_consent_flag: state.toggleState,
        },
      } as EventWhatsappCommsToggleSelect);
    }
  }, [whatsAppOptType]);

  function handleChange({ name, value }: any) {
    setForm({ ...form, [name]: value });
  }

  function handleConsentChange({ checked }: { checked: boolean }) {
    if (!checked) {
      setState({ ...state, toggleState: !checked });
      whatsAppBody.phoneNumber = form.mobileNumber;
      dispatch(whatsAppOptIn(whatsAppBody));
    } else {
      setState({ ...state, showRequestModal: true });
    }
  }

  function handleCancel() {
    dispatch(updateAuthState({ whatsAppOptType: '' }));
  }

  function handleRequestOptAction({ action }: any) {
    switch (action) {
      case 'confirm': {
        setState({ ...state, toggleState: false });
        whatsAppBody.phoneNumber = form.mobileNumber;
        dispatch(whatsAppOptOut(whatsAppBody));
      }
        break;
      case 'reject':
        setState({ ...state, showRequestModal: false, toggleState: true });
        break;
    }
  }

  function handleLogout() {
    if (accessToken)
      dispatch(logout(accessToken));
  }

  function handleBackClick() {
    history.goBack();
  }

  function handleSubmit() {
    if (userUpdateStatus === Status.pending) return;
    const {
      houseNo,
      landmark,
      pinCode,
      ...rest
    } = form;
    dispatch(updateUser({
      ...user, ...rest, address: {
        houseNo,
        landmark,
        pinCode,
      },
    }));
  }

  function commonInputProps(name: keyof typeof Labels) {
    return {
      key: name,
      name,
      label: Labels[name],
      value: form[name],
      enableValueSanitize: true,
      rootClassName: 'inputRootClassName',
      onChange: handleChange,
      onBlur: handleChange,
    };
  }

  return (
    <Root>
      <Header>
        <Icons.BackIcon className='clickable' hideBorder onClick={handleBackClick} />
        <Heading4 tagAs='p' fontWeight='bold' title='Profile' />
        <PrimaryButton
          title='Save'
          size='small'
          icon={userUpdateStatus === Status.pending ? 'loading' : ''}
          onClick={handleSubmit}
        />
      </Header>

      <Container>
        <BaseBox flex={1}>
          <Avatar mb='16px' size='48px' src={avatar} />
          <Body2 mb='4px' title='Mobile Number' />
          <SubText fontWeight='bolder' title={`+91 ${form.mobileNumber}`} />

          <Form>
            <DoconInput
              autoFocus
              {...commonInputProps('firstName')}
            />

            <DoconInput {...commonInputProps('lastName')} />

            <FlexBox flex={'1 1 100%'}>
              <DoconDatePicker
                disableFuture
                format={'DD/MM/yyyy'}
                placeholder='dd/mm/yyyy'
                {...commonInputProps('dateOfBirth')}
              />

              <FlexBox vertical verticalSpace={8} ml='12px'>
                <DoconLabel label='Gender *' />
                <DoconRadioButton
                  vertical={false}
                  options={[
                    { label: 'Male', value: 'Male' },
                    { label: 'Female', value: 'Female' },
                  ]}
                  className='radioButton'
                  {...commonInputProps('gender')}
                  selected={form.gender}
                />
              </FlexBox>
            </FlexBox>

            <DoconInput
              {...commonInputProps('email')}
              type='email'
              enableValueSanitize={false}
            />

            <DoconInput {...commonInputProps('houseNo')} />

            <DoconInput {...commonInputProps('landmark')} />

            <DoconInput {...commonInputProps('pinCode')} />
          </Form>

          <Heading4 mt='24px' fontWeight={600} title='Notification settings' />
          <Row>
            <WhatsAppIcon width={24} />
            <SubText title='Receive updates on WhatsApp' />
            <DoconToggle
              disabled={false}
              checked={state.toggleState}
              onClick={handleConsentChange}
            />
          </Row>
          <Row className='clickable' onClick={handleLogout}>
            <LogoutIcon width={24} />
            <SubText title='Logout' />
          </Row>

          <RequestSuccessfulModal
            visible={whatsAppOptType === 'opt-in' || whatsAppOptType === 'opt-out'}
            requestType={whatsAppOptType}
            onCancel={handleCancel}
          />
          <RequestOptOutModal
            visible={state.showRequestModal}
            onClick={handleRequestOptAction}
          />

        </BaseBox>

        <Footer>
          <Banner>
            <BaseBox flex={1}>
              <SubText title='Chat for FREE with your doctor for post consultation queries' />
              <PrimaryButton
                title='Get App'
                size='small'
                onClick={downloadAppPromotion}
              />
            </BaseBox>

            <Avatar size='40px' src={avatar} />
          </Banner>

          <Body2 title={'Copyright © 2020, Docon Technologies Pvt Ltd'} />
        </Footer>
      </Container>
    </Root>
  );
}

export default Logout;